/**
 * NavigationMenu Component
 * Displays top-level navigation menu items in the center of the header
 * Supports keyboard navigation and marks current page with aria-current
 */

import React, { useCallback } from 'react';
import { NavigationMenuProps } from './types';
import styles from './AppHeader.module.css';

/**
 * Renders navigation menu items in horizontal layout
 * Sorts items by order property, marks current item with aria-current="page"
 */
export const NavigationMenu: React.FC<NavigationMenuProps> = ({
  items,
  currentPath,
  onMenuItemSelected,
  className,
}) => {
  // Sort items by order property
  const sortedItems = [...items].sort((a, b) => a.order - b.order);

  const handleMenuItemClick = useCallback(
    (item) => {
      if (onMenuItemSelected) {
        onMenuItemSelected(item);
      }
    },
    [onMenuItemSelected],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>, item) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleMenuItemClick(item);
      }
    },
    [handleMenuItemClick],
  );

  return (
    <nav
      className={`${styles.navigationMenu} ${className || ''}`}
      aria-label="Main navigation"
      data-testid="navigation-menu"
    >
      <ul className={styles.menuList} data-testid="menu-items-list">
        {sortedItems.map((item) => {
          const isActive = currentPath === item.path;
          return (
            <li key={item.id} className={styles.menuItem}>
              <button
                className={`${styles.menuButton} ${isActive ? styles.active : ''}`}
                onClick={() => handleMenuItemClick(item)}
                onKeyDown={(e) => handleKeyDown(e, item)}
                aria-current={isActive ? 'page' : undefined}
                data-testid={`menu-item-${item.id}`}
              >
                {item.icon && <span className={styles.menuIcon}>{item.icon}</span>}
                <span className={styles.menuLabel}>{item.label}</span>
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
};

export default NavigationMenu;
